import { TechnicalRequirementsFloatVO, TechnicalRequirementsLongVO, TechnicalRequirementsStringVO, TechnicalRequirementsVO } from '@article/properties/technical/technical-requirements';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';

export const potentials = ['L', 'N', 'PE'] as const;

export type Potential = typeof potentials[number];

export const distributionActuationTypeValues = ['Push-button', 'Operating tool'] as const;
export type DistributionActuationType = typeof actuationTypeValues[number];

const actuationTypeValues = ['Lever', ...distributionActuationTypeValues] as const;
export type ActuationType = typeof actuationTypeValues[number];

export const supplyActuationTypeValues = actuationTypeValues;
export type SupplyActuationType = ActuationType;

export const supplyCrossSectionValues = [35, 16, 10, 6, 4, 2.5] as const;
export type SupplyCrossSection = typeof supplyCrossSectionValues[number];

class DistributionTerminalsSupplyModel {
  public totalCurrent?: number;

  get crossSectionValues(): readonly SupplyCrossSection[] {
    if (!this.totalCurrent) {
      return [];
    }
    if (this.totalCurrent > 76) {
      return [35];
    }
    if (this.totalCurrent > 57) {
      return [35, 16]
    }
    if (this.totalCurrent > 41) {
      return [35, 16, 10];
    }
    if (this.totalCurrent > 32) {
      return [35, 16, 10, 6];
    }
    if (this.totalCurrent > 24) {
      return [35, 16, 10, 6, 4];
    }
    return supplyCrossSectionValues;
  }

  public crossSection?: SupplyCrossSection;

  get actuationTypeValues(): readonly SupplyActuationType[] {
    return this.crossSection === 35 ? ['Operating tool'] : supplyActuationTypeValues;
  }

  public actuationType?: SupplyActuationType;

  get inputValues() {
    return this.actuationType === 'Lever' ? [1, 2] : [1];
  }

  public inputs?: number;
}

export const distributionCrossSectionValues = [16, 10, 6, 4, 2.5, 1.5, 1] as const;
export type DistributionCrossSection = typeof distributionCrossSectionValues[number];

export const distributionAlignmentValues = ['left', 'symmetric', 'right'] as const;
export type DistributionAlignment = typeof distributionAlignmentValues[number];

class DistributionTerminalsDistributionModel {

  constructor(private supply: DistributionTerminalsSupplyModel) {
  }

  public get actuationTypeValues(): readonly DistributionActuationType[] {
    if (!this.supply.actuationType) {
      return [];
    }
    if (this.supply.actuationType === 'Lever') {
      return distributionActuationTypeValues;
    }
    return [this.supply.actuationType];
  }
  public actuationType?: DistributionActuationType;

  public get crossSectionValues() {
    return distributionCrossSectionValues
      .filter(value =>
        this.isSmallerEqualThanInput(value)
        && this.isNotTooSmallComparedToInput(value));
  }

  public crossSection?: DistributionCrossSection;
  public outputs?: number;

  get alignmentValues(): readonly DistributionAlignment[] {
    if (this.supply.crossSection === 2.5
      && (this.crossSection === 1.5 || this.crossSection === 1.0)) {
      return ['left'];
    }

    const totalCurrentOfJumper = this.getTotalCurrentOfJumper();
    if (totalCurrentOfJumper && this.supply.totalCurrent && totalCurrentOfJumper < this.supply.totalCurrent) {
      return ['symmetric'];
    }

    return distributionAlignmentValues;
  }

  public alignment?: DistributionAlignment;

  private isSmallerEqualThanInput(diameter: DistributionCrossSection) {
    return this.supply.crossSection && diameter <= this.supply.crossSection;
  }

  private isNotTooSmallComparedToInput(diameter: DistributionCrossSection) {
    switch (this.supply.crossSection) {
      case 35:
        return diameter >= 10;
      case 16:
      case 10:
        return diameter >= 2.5;
      case 6:
      case 4:
        return diameter >= 1.5;
      case 2.5:
        return true;
    }
  }

  private getTotalCurrentOfJumper() {
    switch (this.supply.crossSection) {
      case 35:
        return 90;
      case 16:
        return this.crossSection === 16 ? 76 : 57;
      case 10:
        return 57;
      case 6:
        return this.crossSection === 6 ? 41 : 32;
      case 4:
        return 32;
      case 2.5:
        switch (this.crossSection) {
          case 2.5:
            return 25;
          case 1.5:
            return 18;
          case 1.0:
            return 14;
        }
    }
  }
}

export type DistributionConnectionCount = 2 | 3 | 4;

export class DistributionTerminalsAssistantModel {

  public potential?: Potential;

  private _supply: DistributionTerminalsSupplyModel = new DistributionTerminalsSupplyModel();

  private _distribution: DistributionTerminalsDistributionModel = new DistributionTerminalsDistributionModel(this._supply);

  public get supply(): DistributionTerminalsSupplyModel {
    return this._supply;
  }

  public get distribution(): DistributionTerminalsDistributionModel {
    return this._distribution;
  }
}

export type DistributionTerminalsAssistantSupplyRequirement =
  Required<DistributionTerminalsSupplyModel>;

export type DistributionTerminalsAssistantDistributionRequirement =
  Required<DistributionTerminalsDistributionModel>;

export type DistributionTerminalsAssistantRequirement = {
  potential: Potential;
  supply: DistributionTerminalsAssistantSupplyRequirement;
  distribution: DistributionTerminalsAssistantDistributionRequirement;
}

export function createSupplyRequirements(requirement: DistributionTerminalsAssistantRequirement): TechnicalRequirementsVO[] {
  const technicalRequirements: TechnicalRequirementsVO[] = [
    new TechnicalRequirementsStringVO('COLOR', mapPotentialToColor(requirement.potential)),
    new TechnicalRequirementsFloatVO('NOMINAL_CROSS_SECTION', requirement.supply.crossSection),
    new TechnicalRequirementsLongVO('TOTAL_NUMBER_OF_CONNECTION_POINTS', 2),
    new TechnicalRequirementsStringVO('ACTUATION_TYPE', requirement.supply.actuationType),
  ];
  // If actuation type of supply is lever, the actuation type of the distribution is relevant.
  if (requirement.supply.actuationType === 'Lever') {
    technicalRequirements.push(new TechnicalRequirementsStringVO('ACTUATION_TYPE_2', requirement.distribution.actuationType));
  }
  return technicalRequirements;
}

export function createDistributionRequirements(requirement: DistributionTerminalsAssistantRequirement): TechnicalRequirementsVO[] {
  return [
    new TechnicalRequirementsStringVO('COLOR', mapPotentialToColor(requirement.potential)),
    new TechnicalRequirementsFloatVO('NOMINAL_CROSS_SECTION', requirement.distribution.crossSection),
    new TechnicalRequirementsStringVO('ACTUATION_TYPE', requirement.distribution.actuationType ?? requirement.supply.actuationType),
    new TechnicalRequirementsLongVO('TOTAL_NUMBER_OF_CONNECTION_POINTS', 2, 3, 4),
  ];
}

export function mapPotentialToColor(potential: Potential) {
  switch (potential) {
    case 'L':
      return 'gray';
    case 'N':
      return 'blue';
    case 'PE':
      return 'green-yellow';
    default:
      assertExhaustiveSwitch(potential);
  }
}

export function isRequirement(model: any): model is DistributionTerminalsAssistantRequirement {
  return model instanceof DistributionTerminalsAssistantModel
    && !!model.potential
    && Object.values(model.supply).every(value => value)
    && Object.values(model.distribution).every(value => value);
}
